import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, Modal, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAuthContext } from "../../../context";
import { ToastContent } from "../../../shared/components";
import { useToast } from "../../../shared/hooks";
import { FinancialService, ISetup } from "../../../shared/services/api/financial/FinancialService";
import { formatDocument, globalMaskReal, globalRemoveMaskRealDecimais } from "../../../shared/utils";

interface ModalClientPaymentSetupProps {
	rowSetup: ISetup;
	isOpenModal: boolean;
	onCloseModal: () => void;
	onSuccess?: () => void;
	selectedSetups?: ISetup[];
}

export const ModalClientPaymentSetup: React.FC<ModalClientPaymentSetupProps> = ({ rowSetup, isOpenModal, onCloseModal, onSuccess, selectedSetups }) => {
	const toastHandler = useToast();
	const { loggedUser } = useAuthContext();
	const [isLoading, setIsLoading] = useState(false);
	const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
	const [processedCount, setProcessedCount] = useState(0);
	const [processingSummary, setProcessingSummary] = useState<{ success: number; error: number } | null>(null);
	const [creditAmountsById, setCreditAmountsById] = useState<Record<string, string>>({});
	const canClientPayment = (status?: string) => status === "paid";

	const setupsToProcess = useMemo(() => (selectedSetups && selectedSetups.length > 0 ? selectedSetups : [rowSetup]), [selectedSetups, rowSetup]);
	const isBatchMode = setupsToProcess.length > 1;
	const currentSetup = setupsToProcess[Math.min(currentProcessingIndex, setupsToProcess.length - 1)] ?? rowSetup;

	const parseNumericValue = (value?: string | number | null): number => {
		const asString = String(value ?? "0");
		let normalized = asString;

		// Heurística:
		// - se vier com vírgula, assume pt-BR (1.234,56) => remove '.' e troca ',' por '.'
		// - se não tiver vírgula, assume '.' como decimal (1234.56)
		if (normalized.includes(",")) {
			normalized = normalized.replace(/\./g, "").replace(",", ".");
		}

		const parsed = Number.parseFloat(normalized);
		return Number.isFinite(parsed) ? parsed : 0;
	};

	const getDefaultCreditAmountMasked = (setup: ISetup): string => {
		const clientAmount = parseNumericValue(setup.client_amount);
		const rawAmount = parseNumericValue(setup.raw_amount);
		const transferPercentage = parseNumericValue(setup.setupTransfer);

		const calculatedTransferAmount = transferPercentage > 0 ? (rawAmount * transferPercentage) / 100 : 0;
		const valueNumber = clientAmount > 0 ? clientAmount : calculatedTransferAmount > 0 ? calculatedTransferAmount : rawAmount;
		const cents = Math.round(valueNumber * 100).toString();
		return globalMaskReal(cents);
	};

	const formatCurrencyLabel = (value?: string | number | null) => {
		const numericValue = parseNumericValue(value);
		return numericValue.toLocaleString("pt-br", { style: "currency", currency: "BRL" });
	};

	const formatPercentageLabel = (value?: string | number | null) => {
		const numericValue = parseNumericValue(value);
		return numericValue > 0 ? `${numericValue.toLocaleString("pt-br", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` : "-";
	};

	const getPaidAmountLabel = (setup: ISetup) => formatCurrencyLabel(setup.credit_amount ?? setup.credited_amount);
	const getClientAmountLabel = (setup: ISetup) => formatCurrencyLabel(setup.client_amount);

	const getSetupKey = (setupId: string | number) => String(setupId);

	const buildDefaultCreditAmounts = (setups: ISetup[]) =>
		setups.reduce<Record<string, string>>((accumulator, setup) => {
			accumulator[getSetupKey(setup.id)] = getDefaultCreditAmountMasked(setup);
			return accumulator;
		}, {});

	const [creditDate, setCreditDate] = useState<string>(() => {
		const raw = rowSetup.credit_date || "";
		if (!raw) return new Date().toISOString().slice(0, 10);
		return raw.slice(0, 10);
	});
	const [note, setNote] = useState<string>("");

	useEffect(() => {
		if (!isOpenModal) return;
		setCreditAmountsById(buildDefaultCreditAmounts(setupsToProcess));
		setCreditDate((prev) => {
			const raw = rowSetup.credit_date || "";
			if (raw) return raw.slice(0, 10);
			return prev;
		});
		setCurrentProcessingIndex(0);
		setProcessedCount(0);
		setProcessingSummary(null);
		setNote("");
	}, [isOpenModal, rowSetup, setupsToProcess]);

	const canConfirm = useMemo(
		() =>
			setupsToProcess.length > 0 &&
			setupsToProcess.every((setup) => {
				const amount = creditAmountsById[getSetupKey(setup.id)] ?? getDefaultCreditAmountMasked(setup);
				return canClientPayment(setup.status) && globalRemoveMaskRealDecimais(amount).trim().length > 0;
			}),
		[creditAmountsById, setupsToProcess]
	);

	const handleCreditAmountChange = (setupId: string | number, value: string) => {
		setCreditAmountsById((prevState) => ({
			...prevState,
			[getSetupKey(setupId)]: globalMaskReal(value),
		}));
	};

	const getDefaultNote = () => {
		const userName = loggedUser?.name?.trim();
		return userName ? `Repasse efetuado por ${userName}` : "Repasse efetuado por usuário não identificado";
	};

	const handleConfirm = async () => {
		if (!canConfirm) return;
		try {
			setIsLoading(true);
			setProcessedCount(0);
			setProcessingSummary(null);

			let successCount = 0;
			let errorCount = 0;
			const noteToSend = note.trim() || getDefaultNote();

			for (let index = 0; index < setupsToProcess.length; index++) {
				const setup = setupsToProcess[index];
				setCurrentProcessingIndex(index);

				const creditAmountValue = creditAmountsById[getSetupKey(setup.id)] ?? getDefaultCreditAmountMasked(setup);
				const result = await FinancialService.PaymentClientSetup(setup.id, {
					creditDate: `${creditDate} 12:00:00`,
					clientAmount: globalRemoveMaskRealDecimais(creditAmountValue),
					note: noteToSend,
				});

				if (result instanceof Error) {
					errorCount++;
				} else {
					successCount++;
				}

				setProcessedCount(index + 1);

				if (index < setupsToProcess.length - 1) {
					await new Promise((resolve) => setTimeout(resolve, 400));
				}
			}

			setProcessingSummary({ success: successCount, error: errorCount });

			if (errorCount > 0 && successCount === 0) {
				throw new Error("Erro ao registrar pagamento ao cliente");
			}

			toastHandler.present({
				type: errorCount === 0 ? "success" : successCount > 0 ? "warning" : "error",
				position: "top-right",
				messageOrContent: (
					<ToastContent
						title={"Processamento concluído"}
						content={`Sucesso: ${successCount} | Erros: ${errorCount}`}
					/>
				),
			});
			onCloseModal();
			onSuccess?.();
		} catch (error) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Erro"} content={"Erro ao registrar pagamento ao cliente"} />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const style = {
		position: "absolute" as const,
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: isBatchMode ? "min(980px, 96vw)" : "min(720px, 92vw)",
		bgcolor: "background.paper",
		border: "1px solid #605e5e",
		boxShadow: 24,
		p: 0,
		borderRadius: "12px",
	};

	return (
		<Modal open={isOpenModal} onClose={onCloseModal} aria-labelledby="modal-client-payment-setup-title">
			<Box sx={style}>
				<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }} id="modal-client-payment-setup-title">
					<Typography variant="h6" color="primary">
						{isBatchMode ? `Repasse em lote da taxa setup (${setupsToProcess.length})` : "Repasse de taxa de setup"}
					</Typography>
					<IconButton onClick={onCloseModal} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					{isLoading && (
						<Box mb={2}>
							<Typography variant="body2" color="primary" sx={{ mb: 1 }}>
								Processando {processedCount + 1} de {setupsToProcess.length}: {currentSetup.tenant_name}
							</Typography>
							<LinearProgress variant="indeterminate" />
						</Box>
					)}
					{processingSummary && !isLoading && (
						<Box mb={2}>
							<Typography variant="body2" color="text.secondary">
								Último processamento: {processingSummary.success} sucesso(s) e {processingSummary.error} erro(s).
							</Typography>
						</Box>
					)}
					<Grid container spacing={2}>
						{!isBatchMode && (
							<>
								<Grid item xs={12} md={6}>
									<Typography variant="subtitle2" color="text.secondary">
										Imobiliária
									</Typography>
									<Typography variant="body1" fontWeight={600}>
										{currentSetup.client_name}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{formatDocument(currentSetup.client_document)}
									</Typography>
								</Grid>

								<Grid item xs={12} md={6}>
									<Typography variant="subtitle2" color="text.secondary">
										Cliente
									</Typography>
									<Typography variant="body1" fontWeight={600}>
										{currentSetup.tenant_name}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{formatDocument(currentSetup.tenant_document)}
									</Typography>
								</Grid>
							</>
						)}

						<Grid item xs={12} md={isBatchMode ? 4 : 6}>
							<TextField
								fullWidth
								size="small"
								label="Data do repasse à imobiliária"
								type="date"
								InputLabelProps={{ shrink: true }}
								value={creditDate}
								onChange={(e) => setCreditDate(e.target.value)}
								disabled={isLoading}
							/>
						</Grid>

						<Grid item xs={12} md={isBatchMode ? 8 : 6}>
							{isBatchMode ? (
								<TextField
									fullWidth
									size="small"
									label="Observação (opcional)"
									value={note}
									onChange={(e) => setNote(e.target.value)}
									disabled={isLoading}
								/>
							) : (
								<TextField
									fullWidth
									size="small"
									label="Valor repassado"
									value={creditAmountsById[getSetupKey(rowSetup.id)] ?? ""}
									onChange={(e) => handleCreditAmountChange(rowSetup.id, e.target.value)}
									disabled={isLoading}
									inputMode="numeric"
								/>
							)}
						</Grid>

						{!isBatchMode && (
							<Grid item xs={12}>
								<TextField
									fullWidth
									size="small"
									label="Observação (opcional)"
									value={note}
									onChange={(e) => setNote(e.target.value)}
									disabled={isLoading}
									multiline
									minRows={3}
								/>
							</Grid>
						)}

						{isBatchMode && (
							<Grid item xs={12}>
								<Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
									<Box sx={{ maxHeight: 420, overflowY: "auto" }}>
										<Table size="small" stickyHeader>
											<TableHead>
												<TableRow>
													<TableCell>Cliente</TableCell>
													<TableCell>Imobiliária</TableCell>
													<TableCell>Valor pago</TableCell>
													<TableCell>Repasse</TableCell>
													<TableCell sx={{ width: 220 }}>Valor do repasse</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{setupsToProcess.map((setup) => (
													<TableRow key={setup.id} hover>
														<TableCell>
															<Typography variant="body2" fontWeight={600}>
																{setup.tenant_name}
															</Typography>
															<Typography variant="caption" color="text.secondary" display="block">
																{formatDocument(setup.tenant_document)}
															</Typography>
														</TableCell>
														<TableCell>
															<Typography variant="body2" fontWeight={600}>
																{setup.client_name}
															</Typography>
															<Typography variant="caption" color="text.secondary" display="block">
																{formatDocument(setup.client_document)}
															</Typography>
														</TableCell>
														<TableCell>
															<Typography variant="body2" fontWeight={600}>
																{getPaidAmountLabel(setup)}
															</Typography>
														</TableCell>
														<TableCell>
															<Typography variant="body2" fontWeight={600}>
																{getClientAmountLabel(setup)}
															</Typography>
															<Typography variant="caption" color="text.secondary" display="block">
																{formatPercentageLabel(setup.setupTransfer)}
															</Typography>
														</TableCell>
														<TableCell>
															<TextField
																fullWidth
																size="small"
																value={creditAmountsById[getSetupKey(setup.id)] ?? getDefaultCreditAmountMasked(setup)}
																onChange={(e) => handleCreditAmountChange(setup.id, e.target.value)}
																disabled={isLoading}
																inputMode="numeric"
															/>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</Box>
								</Box>
							</Grid>
						)}
					</Grid>

					{isBatchMode && (
						<Box mt={2}>
							<Typography variant="body2" color="text.secondary">
								Os valores foram carregados com o total de cada título e podem ser ajustados individualmente antes da confirmação.
							</Typography>
						</Box>
					)}

					{!canConfirm && (
						<Box mt={2}>
							<Typography variant="body2" color="text.secondary">
								O repasse só pode ser confirmado quando todos os registros estiverem com status "Pago" ao cliente.
							</Typography>
						</Box>
					)}
				</DialogContent>

				<DialogActions sx={{ justifyContent: "center", gap: 2, p: 2 }}>
					<Button onClick={onCloseModal} color="inherit" variant="contained" disabled={isLoading}>
						Voltar
					</Button>
					<Button onClick={handleConfirm} color="primary" variant="contained" disabled={isLoading || !canConfirm}>
						Confirmar
					</Button>
				</DialogActions>
			</Box>
		</Modal>
	);
};
